import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Bell, BellRing, Plus, Trash2, Edit2, TrendingUp, TrendingDown, Mail, Smartphone, Check, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../i18n';
import api from '../lib/api';

const RateAlerts = ({ currencies = [] }) => {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, triggered: 0, inactive: 0 });
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    from_currency: 'EUR',
    to_currency: 'USD',
    target_rate: '',
    condition: 'above',
    notification_method: 'both',
    note: ''
  });

  useEffect(() => {
    fetchAlerts();
    fetchStats();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/api/exchange/alerts/');
      setAlerts(response.data);
    } catch (error) {
      logger.error('Error fetching alerts:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/exchange/alerts/summary/stats');
      setStats(response.data);
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  const handleCreateAlert = async () => {
    if (!formData.target_rate || parseFloat(formData.target_rate) <= 0) {
      toast.error('Please enter a valid target rate');
      return;
    }

    if (formData.from_currency === formData.to_currency) {
      toast.error('Currencies must be different');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/exchange/alerts/', {
        ...formData,
        target_rate: parseFloat(formData.target_rate)
      });
      toast.success(t('rateAlerts.alertCreated'));
      setCreateDialogOpen(false);
      setFormData({
        from_currency: 'EUR',
        to_currency: 'USD',
        target_rate: '',
        condition: 'above',
        notification_method: 'both',
        note: ''
      });
      fetchAlerts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create alert');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAlert = async (alertId, currentStatus) => {
    try {
      await api.post(`/api/exchange/alerts/${alertId}/toggle`);
      fetchAlerts();
      fetchStats();
      toast.success(currentStatus ? 'Alert deactivated' : 'Alert activated');
    } catch (error) {
      toast.error('Failed to toggle alert');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await api.delete(`/api/exchange/alerts/${alertId}`);
      toast.success(t('rateAlerts.alertDeleted'));
      fetchAlerts();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const getStatusBadge = (alert) => {
    if (alert.is_triggered) {
      return <Badge variant="default" className="bg-emerald-500">{t('rateAlerts.triggered')}</Badge>;
    }
    if (alert.is_active) {
      return <Badge variant="outline" className="border-blue-500 text-blue-500">{t('rateAlerts.active')}</Badge>;
    }
    return <Badge variant="secondary">{t('rateAlerts.inactive')}</Badge>;
  };

  const getNotificationIcon = (method) => {
    switch (method) {
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'push':
        return <Smartphone className="w-4 h-4" />;
      case 'both':
        return (
          <div className="flex gap-1">
            <Mail className="w-3 h-3" />
            <Smartphone className="w-3 h-3" />
          </div>
        );
      default:
        return null;
    }
  };

  // Get popular currencies for quick selection
  const popularCurrencies = currencies.length > 0 
    ? currencies.filter(c => ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD'].includes(c.code))
    : [
        { code: 'USD', symbol: '$' },
        { code: 'EUR', symbol: '€' },
        { code: 'GBP', symbol: '£' },
        { code: 'JPY', symbol: '¥' },
        { code: 'CHF', symbol: 'Fr' },
        { code: 'CAD', symbol: 'C$' },
        { code: 'AUD', symbol: 'A$' }
      ];

  return (
    <Card data-testid="rate-alerts-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BellRing className="w-5 h-5 text-primary" />
            <CardTitle className="font-heading text-lg">{t('rateAlerts.title')}</CardTitle>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1" data-testid="create-alert-btn">
                <Plus className="w-4 h-4" />
                {t('rateAlerts.createAlert')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t('rateAlerts.createAlert')}</DialogTitle>
                <DialogDescription>
                  {t('rateAlerts.noAlertsDesc')}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Currency Pair Selection */}
                <div className="grid grid-cols-5 gap-2 items-center">
                  <div className="col-span-2">
                    <Label className="text-xs text-muted-foreground">{t('exchange.from')}</Label>
                    <Select 
                      value={formData.from_currency} 
                      onValueChange={(v) => setFormData({...formData, from_currency: v})}
                    >
                      <SelectTrigger data-testid="alert-from-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(currencies.length > 0 ? currencies : popularCurrencies).map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-center pt-5">
                    <span className="text-muted-foreground">/</span>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-muted-foreground">{t('exchange.to')}</Label>
                    <Select 
                      value={formData.to_currency} 
                      onValueChange={(v) => setFormData({...formData, to_currency: v})}
                    >
                      <SelectTrigger data-testid="alert-to-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(currencies.length > 0 ? currencies : popularCurrencies).map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Condition Selection */}
                <div className="space-y-2">
                  <Label>{t('rateAlerts.notifyMe')}</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.condition === 'above' ? 'default' : 'outline'}
                      className="flex-1 gap-2"
                      onClick={() => setFormData({...formData, condition: 'above'})}
                      data-testid="condition-above"
                    >
                      <TrendingUp className="w-4 h-4" />
                      {t('rateAlerts.above')}
                    </Button>
                    <Button
                      type="button"
                      variant={formData.condition === 'below' ? 'default' : 'outline'}
                      className="flex-1 gap-2"
                      onClick={() => setFormData({...formData, condition: 'below'})}
                      data-testid="condition-below"
                    >
                      <TrendingDown className="w-4 h-4" />
                      {t('rateAlerts.below')}
                    </Button>
                  </div>
                </div>

                {/* Target Rate */}
                <div className="space-y-2">
                  <Label>{t('rateAlerts.targetRate')}</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    placeholder="1.1000"
                    value={formData.target_rate}
                    onChange={(e) => setFormData({...formData, target_rate: e.target.value})}
                    className="font-mono"
                    data-testid="target-rate-input"
                  />
                </div>

                {/* Notification Method */}
                <div className="space-y-2">
                  <Label>{t('rateAlerts.notifyVia')}</Label>
                  <div className="flex gap-2">
                    {['email', 'push', 'both'].map((method) => (
                      <Button
                        key={method}
                        type="button"
                        variant={formData.notification_method === method ? 'default' : 'outline'}
                        className="flex-1 gap-1"
                        onClick={() => setFormData({...formData, notification_method: method})}
                        data-testid={`notify-${method}`}
                      >
                        {getNotificationIcon(method)}
                        {t(`rateAlerts.${method}`)}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Note */}
                <div className="space-y-2">
                  <Label>{t('rateAlerts.note')}</Label>
                  <Input
                    placeholder="Optional note..."
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    data-testid="alert-note-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleCreateAlert} disabled={loading} data-testid="submit-alert-btn">
                  {loading ? 'Creating...' : t('rateAlerts.createAlert')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        
        {/* Stats */}
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">{t('rateAlerts.active')}:</span>
            <span className="font-medium text-blue-500">{stats.active}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">{t('rateAlerts.triggered')}:</span>
            <span className="font-medium text-emerald-500">{stats.triggered}</span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {alerts.length === 0 ? (
          <div className="text-center py-8 px-4">
            <Bell className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">{t('rateAlerts.noAlerts')}</p>
            <p className="text-sm text-muted-foreground/70 mt-1">{t('rateAlerts.noAlertsDesc')}</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[300px]">
            <AnimatePresence>
              {alerts.map((alert, index) => (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center justify-between px-4 py-3 border-b last:border-0 ${
                    alert.is_triggered ? 'bg-emerald-50 dark:bg-emerald-900/10' : ''
                  } ${!alert.is_active ? 'opacity-60' : ''}`}
                  data-testid={`alert-item-${alert.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      alert.condition === 'above' 
                        ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                        : 'bg-red-100 dark:bg-red-900/30'
                    }`}>
                      {alert.condition === 'above' 
                        ? <TrendingUp className="w-5 h-5 text-emerald-600" />
                        : <TrendingDown className="w-5 h-5 text-red-600" />
                      }
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{alert.from_currency}/{alert.to_currency}</span>
                        {getStatusBadge(alert)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t('rateAlerts.whenRate')} {alert.condition === 'above' ? '≥' : '≤'}{' '}
                        <span className="font-mono">{alert.target_rate.toFixed(4)}</span>
                        <span className="mx-2">•</span>
                        {t('rateAlerts.currentRate')}: <span className="font-mono">{alert.current_rate?.toFixed(4) || '-'}</span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="text-muted-foreground">
                      {getNotificationIcon(alert.notification_method)}
                    </div>
                    <Switch
                      checked={alert.is_active}
                      onCheckedChange={() => handleToggleAlert(alert.id, alert.is_active)}
                      data-testid={`toggle-alert-${alert.id}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDeleteAlert(alert.id)}
                      data-testid={`delete-alert-${alert.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default RateAlerts;
